/**
 * One dish on the table board.
 *
 * Pressing the name opens the dish — photograph and ingredients. Changing its
 * state is the explicit button on the right: a cook was advancing statuses by
 * touching the dish name, which is the wrong thing to happen by accident.
 *
 * A dish whose wave the waiter has not called is shown, greyed, with no button
 * and an explanation. It is not the kitchen's yet.
 */
import { ChevronRight, CheckCircle2 } from 'lucide-react';
import type { DelayTier } from '@shared/kds/table-board';
import DishRowTimers from './DishRowTimers';
import { isOffMenuItem, itemDisplayName, OFF_MENU_TAG } from '../lib/off-menu';
import DinerTagChip from './DinerTagChip';
import { useDishRowActions } from '../hooks/useDishRowActions';
import { dishRowState, secsSince } from '../lib/dish-row';
import { useTranslation } from '../i18n';

interface TableDishRowProps {
  item: any;
  /** Lateness colouring. "normal" means no styling. */
  delayTier?: DelayTier;
  /** The single oldest waiting dish on the whole board. */
  isOldest?: boolean;
  /** Preview of something already gone out: struck through, dimmed, no controls. */
  served?: boolean;
  now?: number;
  /**
   * Bar board: a drink with a recipe keeps "Inizia → Pronto"; a bottle out of
   * the fridge gets one "Servito".
   */
  bar?: boolean;
  /**
   * Opens this dish on its own. Owned by the surface the row sits on — the card
   * or the full-screen comanda — so closing the dish returns the cook to
   * whichever of the two they came from (FR-020).
   */
  onOpenDish?: (item: any) => void;
  /**
   * Feature 252 — this row stands for several identical portions. The count is
   * what the cook reads; the portions are what the actions apply to.
   */
  /** Feature 268 — the venue shows who each plate is for. */
  showDinerTag?: boolean;
  groupCount?: number;
  groupPortions?: readonly any[];
  /** "2 di 3 pronti" — shown only when the portions are not all at one status. */
  groupStatusHint?: string | null;
  /** Opens the line into its portions. Absent on a line of one. */
  onToggleExpanded?: () => void;
  expanded?: boolean;
}

export default function TableDishRow({
  item,
  delayTier = 'normal',
  isOldest = false,
  served = false,
  now = Date.now(),
  bar = false,
  onOpenDish,
  groupCount,
  groupPortions,
  groupStatusHint = null,
  onToggleExpanded,
  expanded = false,
  showDinerTag = false,
}: TableDishRowProps) {
  const { t } = useTranslation();
  const { loading, advance, serve } = useDishRowActions(item, groupPortions);
  const state = dishRowState({ item, served, bar, delayTier, t });

  const dishName: string = itemDisplayName(item);
  const offMenu: boolean = isOffMenuItem(item);
  const variantName: string | undefined = item.variant?.name ?? item.variantName;
  // Feature 252 — a grouped line shows how many portions it stands for. Falls
  // back to the row's own quantity, so an ungrouped board is unchanged.
  const quantity: number = groupCount ?? Number(item.quantity) ?? 1;

  return (
    <div
      className={[
        'flex items-start justify-between py-1.5 px-2.5 rounded-lg',
        'bg-kds-card/50 transition-colors',
        served ? 'opacity-40 line-through' : '',
        state.isPendingUnfired ? 'opacity-50' : '',
        !served && item.status === 'PREPARING' ? 'bg-kds-preparing/10 animate-preparing' : '',
        !served && item.status === 'READY' ? 'bg-kds-ready/10' : '',
        state.delayClass,
        loading ? 'opacity-60' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-x-2 gap-y-0.5 flex-wrap leading-snug">
          {quantity > 1 && <span className="text-kds-qty text-kds-text shrink-0">×{quantity}</span>}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDish?.(item);
            }}
            // Three lines, not two: on a narrow card beside its button a dish
            // name cut at "Risotto ai frutti di…" is a dish nobody can cook.
            className="text-kds-dish text-kds-text leading-snug line-clamp-3 text-left hover:underline cursor-pointer"
            title={t('kds.dishRow.showDish', 'Mostra il piatto: foto e ingredienti')}
          >
            {dishName}
          </button>
          {/* 248 — the anchor dish's name demoted to a tag: the title is what
              the waiter wrote, and this only says the plate is off the menu. */}
          {offMenu && (
            <span
              className="text-kds-badge uppercase tracking-wider text-kds-muted bg-kds-muted/15 px-2 py-0.5 rounded shrink-0"
              title={t('kds.offMenu.hint', 'Piatto scritto dal cameriere al tavolo: la nota è tutta la ricetta')}
            >
              {OFF_MENU_TAG(t)}
            </span>
          )}
          {/* 268 — who it goes to. Nothing at all when the line carries no
              diner or the venue has the feature off. */}
          <DinerTagChip item={item} enabled={showDinerTag} />
          {variantName && (
            <span className="text-kds-note text-kds-muted italic shrink-0">◆ {variantName}</span>
          )}
          {onToggleExpanded && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpanded();
              }}
              className="text-kds-badge uppercase tracking-wider text-kds-muted bg-kds-surface px-2 py-1 rounded shrink-0 hover:text-kds-text"
              title={
                expanded
                  ? t('kds.dishRow.collapsePortions', 'Richiudi le porzioni')
                  : t('kds.dishRow.expandPortions', 'Apri le porzioni per agire su una sola')
              }
            >
              {expanded
                ? t('kds.dishRow.collapse', 'Richiudi')
                : t('kds.dishRow.openPortions', 'Apri le {{count}}', { count: quantity })}
            </button>
          )}
          {groupStatusHint && (
            <span
              className="text-kds-badge text-kds-muted shrink-0"
              title={t('kds.dishRow.portionsStatus', 'Stato delle singole porzioni')}
            >
              {groupStatusHint}
            </span>
          )}
          {isOldest && (
            <span
              className="text-kds-badge uppercase tracking-wider text-kds-priority bg-kds-priority/10 px-2 py-0.5 rounded shrink-0"
              title={t('kds.dishRow.oldestTitle', 'Piatto in attesa da più tempo')}
            >
              {t('kds.dishRow.oldest', '★ Più vecchio')}
            </span>
          )}
          {item.fixedMenuAssignmentId && (
            <span
              className="text-kds-badge uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded shrink-0"
              title={t('kds.dishRow.fixedMenuTitle', 'Incluso nel menu a prezzo fisso')}
            >
              {t('kds.dishRow.fixedMenu', '★ Menu Fisso')}
            </span>
          )}
        </div>

        {item.modifications && (
          <p className="text-kds-note text-kds-preparing font-bold leading-tight mt-0.5">
            &#x26A0; {item.modifications}
          </p>
        )}
        {item.notes && (
          <p className="text-kds-note text-kds-muted leading-tight mt-0.5">{item.notes}</p>
        )}
        {item.isGlutenFreeVersion && (
          <div className="text-kds-note font-bold text-kds-preparing">{t('kds.dish.glutenFree', '⚠ SENZA GLUTINE')}</div>
        )}
        {item.movedFromTableLabel && (
          <p className="text-kds-note text-kds-muted leading-tight mt-0.5">
            {t('kds.dishRow.movedFrom', '↪ spostato da {{table}}', { table: item.movedFromTableLabel })}
          </p>
        )}

        {!served && (
          <DishRowTimers
            waitSecs={state.isFired ? secsSince(item.firedAt, now) : null}
            prepSecs={secsSince(item.prepStartedAt, now)}
            readySecs={secsSince(item.completedAt, now)}
            status={item.status}
            delayTier={delayTier}
          />
        )}
      </div>

      <div className="ml-2 flex items-center gap-2 shrink-0">
        <span
          className={`inline-block w-4 h-4 rounded-full ${state.dotClass} shrink-0`}
          title={state.label}
        />

        {state.canAdvance && !loading && (
          <button
            type="button"
            onClick={advance}
            className={[
              'flex items-center justify-center gap-1.5 px-3 rounded-lg text-kds-action transition-colors',
              'min-h-touch min-w-touch',
              item.status === 'PREPARING'
                ? 'bg-kds-preparing text-black hover:brightness-110'
                : 'bg-kds-card text-kds-text hover:bg-kds-card/70',
            ].join(' ')}
            title={
              item.status === 'PREPARING'
                ? t('kds.dishRow.markReady', 'Segna come pronto')
                : t('kds.dishRow.startPrep', 'Inizia preparazione')
            }
          >
            {item.status === 'PREPARING' ? (
              <>
                <CheckCircle2 size={20} />
                {t('kds.status.ready', 'Pronto')}
              </>
            ) : (
              <>
                <ChevronRight size={20} />
                {t('kds.dishRow.startShort', 'Inizia')}
              </>
            )}
          </button>
        )}

        {state.canServe && !loading && (
          <button
            type="button"
            onClick={serve}
            className="flex items-center justify-center gap-1.5 px-3 rounded-lg text-kds-action min-h-touch min-w-touch bg-kds-ready text-black hover:brightness-110 transition-colors"
            title={t('kds.dishRow.markServed', 'Segna come servito')}
          >
            <CheckCircle2 size={20} />
            {t('kds.status.served', 'Servito')}
          </button>
        )}

        {loading && (
          <span className="w-8 h-8 border-4 border-kds-muted border-t-transparent rounded-full animate-spin" />
        )}

        {/* Not workable yet, and the row says why: the waiter has not sent this
            course into the kitchen. */}
        {state.isPendingUnfired && (
          <span
            className="flex flex-col items-end text-kds-badge text-kds-muted italic leading-tight text-right"
            title={t('kds.dishRow.courseNotSentTitle', 'La portata non è ancora stata inviata in cucina dal cameriere')}
          >
            <span>{t('kds.table.waiting', '⏳ In attesa')}</span>
            <span className="text-kds-meta not-italic font-normal">{t('kds.dishRow.courseNotSent', 'portata non inviata')}</span>
          </span>
        )}

        {item.status === 'READY' && !served && !state.serveOnly && (
          <span className="inline-flex items-center gap-2 text-kds-action text-kds-ready">
            <CheckCircle2 size={20} /> {t('kds.status.ready', 'Pronto')}
          </span>
        )}
      </div>
    </div>
  );
}
